import _ from "lodash";
import React from "react";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";
import classNames from "classnames";
import MoreItemWithToolTip from "UIComponents/MoreItemWithToolTip";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import ACLStore from "lib/aclStore";

import {
  showStudentMessagingAccessPoint,
  showTeacherMessagingAccessPoint,
  showFamilyMessagingAccessPoint,
} from "Settings/modules/SettingsUtils";

import { CommentSquareLeftClassroomOutlined } from "@toddle-edu/ds-icons";

import {
  Avatar,
  ButtonDisclosure,
  Dropdown,
  IconButton,
} from "@toddle-edu/ds-web";

import UserNameAvatar from "AppComponents/UserNameAvatar";

import { useI18n } from "Hooks";
import { getUserType } from "modules/CommonSelectors";

import {
  MESSAGING_MODULE_OPENED_FROM,
  getFormattedNameForUserType,
  recordMessagingChatAccessed,
  recordMessagingModuleOpened,
  isCreateAndManageOwnChannelEnabled,
  isPaidConnectModule,
  isMessagingV2Enabled,
  isMessagingV2ChannelEnabled,
} from "MessagingV2/modules/Utils";

import { isIdEqual } from "Utils/idHelpers";

import StudentAlertsSection from "./StudentAlertsSection";
import classes from "./CommonComponents.scss";

const moreItemOverrideClasses = {
  container: "text-body-s",
  moreContainer: "text-body-s",
};

const styles = {
  dropdownMenuStyle: {
    maxWidth: "260px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  smallAvatarStyles: {
    size: "xx-small",
    shade: "light",
    randomizeColor: true,
    shape: "square",
  },
  profileTextStyle: {
    color: "var(--text-primary)",
    maxWidth: "276px",
    display: "-webkit-box",
  },
  tooltipOverlayStyle: {
    zIndex: 1250,
  },
  profileCellStyle: {
    gridColumnGap: "8px",
  },
};

export const ACCESS_POINT_MODULE = {
  ANNOUNCEMENT: "announcement",
  ATTENDANCE: "attendance",
  BEHAVIOUR: "behaviour",
  AUTHORIZATION_AND_EVALUATION: "authorization_and_evaluation",
  STUDENT_GOAlS: "student_goals",
  INSIGHT: "insight",
  ADMIN_PORTAL: "admin_portal",
  CLASS: "class",
};

export const ACCESS_POINT_PERMISSION = {
  [ACCESS_POINT_MODULE.ANNOUNCEMENT]:
    "FeatureFlag:AnnouncementMessagingAccessPoint",
  [ACCESS_POINT_MODULE.ATTENDANCE]:
    "FeatureFlag:AttendanceMessagingAccessPoint",
  [ACCESS_POINT_MODULE.BEHAVIOUR]: "FeatureFlag:BehaviourMessagingAccessPoint",
  [ACCESS_POINT_MODULE.AUTHORIZATION_AND_EVALUATION]:
    "FeatureFlag:AuthorizationAndEvaluationMessagingAccessPoint",
  [ACCESS_POINT_MODULE.STUDENT_GOAlS]:
    "FeatureFlag:StudentGoalsMessagingAccessPoint",
  [ACCESS_POINT_MODULE.INSIGHT]: "FeatureFlag:InsightMessagingAccessPoint",
  [ACCESS_POINT_MODULE.ADMIN_PORTAL]:
    "FeatureFlag:AdminPortalMessagingAccessPoint",
  [ACCESS_POINT_MODULE.CLASS]: "FeatureFlag:ClassMessagingAccessPoint",
};

const getActionHeader = ({ type, isFromStudent, t }) => {
  switch (type) {
    case STUDENT: {
      const primaryLabel = isFromStudent
        ? "common:send_message_to_the_label"
        : "common:send_message_to_a_label";

      return t(primaryLabel, {
        label: t("common:student_lowercase"),
      });
    }

    case PARENT: {
      const primaryLabel = isFromStudent
        ? "common:send_message_to_a_label"
        : "common:send_message_to_the_label";

      return t(primaryLabel, {
        label: t("common:family_member"),
      });
    }
    default:
      return null;
  }
};

export const PersonalChats = ({
  members,
  onUserClick,
  isLoading,
  type,
  showDivider = false,
  isFromStudent,
}) => {
  const { t } = useI18n(["common"]);
  const currentUserType = getUserType();

  const label = getActionHeader({ type, isFromStudent, t });

  if (isLoading) {
    return (
      <>
        <div className={classes.popoverListLabel}>
          <SkeletonLoader height={20} width="60%" />
        </div>
        <div className={classNames(classes.dropdownItem, classes.autoCursor)}>
          <SkeletonLoader height={40} width="100%" />
        </div>
        {showDivider ? <div className={classes.divider} /> : null}
      </>
    );
  }

  if (_.isEmpty(members)) {
    return null;
  }

  return (
    <>
      <div className={classNames("text-label", classes.popoverListLabel)}>
        {label}
      </div>

      {_.map(members, member => {
        const { isArchived, id, profileImage, name, alertTypes = [] } = member;
        const onClick = () => {
          recordMessagingChatAccessed({
            from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
            currentUserType,
            recipientUserType: type,
          });
          onUserClick(id);
        };

        const studentAlertsConfig =
          type === STUDENT
            ? {
                alertTypes,
                studentId: id,
              }
            : undefined;

        const handleDropdownItemKeyDown = e => {
          onEnterOrSpaceKeyDown(e, onClick);
        };

        return (
          <div
            className={classes.dropdownItem}
            onClick={onClick}
            key={id}
            role="button"
            tabIndex="0"
            aria-label={name}
            onKeyDown={handleDropdownItemKeyDown}
          >
            <UserNameAvatar
              id={id}
              profileImage={profileImage}
              name={name}
              viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
              shouldDynamicColor={true}
              isArchived={isArchived}
              showAvatar={true}
              avatarStyles={styles.smallAvatarStyles}
              showArchivedText={true}
              showTooltip={false}
              profileTextStyle={styles.profileTextStyle}
              profileCellStyle={styles.profileCellStyle}
              studentAlertsConfig={studentAlertsConfig}
            />
          </div>
        );
      })}
      {showDivider ? <div className={classes.divider} /> : null}
    </>
  );
};

export const FamilyChannels = ({
  familyChannels = [],
  onChannelClick,
  isLoading,
  showDivider,
}) => {
  const { t } = useI18n(["common"]);

  if (isLoading) {
    return (
      <>
        <div className={classes.popoverListLabel}>
          <SkeletonLoader height={20} width="60%" />
        </div>
        <div className={classNames(classes.dropdownItem, classes.autoCursor)}>
          <SkeletonLoader height={40} width="100%" />
        </div>
      </>
    );
  }

  if (_.isEmpty(familyChannels)) {
    return null;
  }

  return (
    <>
      <div className={classNames("text-label", classes.popoverListLabel)}>
        {t("common:send_message_to_label", {
          label: t("common:family_channel"),
        })}
      </div>
      {_.map(familyChannels, familyChannel => {
        const { id, label, membersCount } = familyChannel;
        const onClick = () => onChannelClick(id);
        const handleKeyDown = e => onEnterOrSpaceKeyDown(e, onClick);
        return (
          <div
            className={classes.dropdownItem}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
          >
            <Avatar
              iconType="group"
              color="violet"
              {...styles.smallAvatarStyles}
            />
            <div
              className={classNames("text-body", classes.familyChannelLabel)}
            >
              {`${label} (${membersCount})`}
            </div>
          </div>
        );
      })}
      {showDivider ? <div className={classes.divider} /> : null}
    </>
  );
};

export const StudentDropdownPopoverContent = ({
  studentInfo,
  familyList,
  isFamilyListLoading = false,
  familyChannels,
  onUserClick = _.noop,
  onChannelClick = _.noop,
  hideChatWithUser,
}) => {
  const isFamilyPresent = !_.isEmpty(familyList) || isFamilyListLoading;

  const showCreateFamilyChannel =
    isMessagingV2ChannelEnabled() &&
    ((familyChannels &&
      isFamilyPresent &&
      isCreateAndManageOwnChannelEnabled()) ||
      isFamilyListLoading);

  const showStudent = isPaidConnectModule() && !hideChatWithUser;

  return (
    <div className={classes.popoverRoot}>
      {showStudent ? (
        <PersonalChats
          members={[studentInfo]}
          onUserClick={onUserClick}
          isLoading={false}
          type={STUDENT}
          isFromStudent
          showDivider={isFamilyPresent}
        />
      ) : null}
      {isFamilyPresent ? (
        <PersonalChats
          members={familyList}
          onUserClick={onUserClick}
          isLoading={isFamilyListLoading}
          type={PARENT}
          isFromStudent
          showDivider={showCreateFamilyChannel}
        />
      ) : null}
      {showCreateFamilyChannel ? (
        <FamilyChannels
          familyChannels={familyChannels}
          familyList={familyList}
          onChannelClick={onChannelClick}
          isLoading={isFamilyListLoading}
        />
      ) : null}
    </div>
  );
};

export const ParentDropdownPopoverContent = ({
  parentInfo,
  studentsList,
  familyChannels,
  onUserClick = _.noop,
  onChannelClick = _.noop,
  isLoading = false,
  hideChatWithUser,
}) => {
  const isStudentsPresent = !_.isEmpty(studentsList) && isPaidConnectModule();

  const showCreateFamilyChannel =
    isMessagingV2ChannelEnabled() &&
    ((familyChannels &&
      !_.isEmpty(familyChannels) &&
      isCreateAndManageOwnChannelEnabled()) ||
      isLoading);

  return (
    <div className={classes.popoverRoot}>
      {parentInfo && !hideChatWithUser ? (
        <PersonalChats
          members={[parentInfo]}
          onUserClick={onUserClick}
          isLoading={false}
          type={PARENT}
          showDivider={showCreateFamilyChannel || isStudentsPresent}
        />
      ) : null}
      {showCreateFamilyChannel ? (
        <FamilyChannels
          familyChannels={familyChannels}
          onChannelClick={onChannelClick}
          isLoading={isLoading}
          isFromParent
          showDivider={isStudentsPresent}
        />
      ) : null}
      {isStudentsPresent ? (
        <PersonalChats
          members={studentsList}
          onUserClick={onUserClick}
          isLoading={false}
          type={STUDENT}
        />
      ) : null}
    </div>
  );
};

export const getUserFooter = ({
  userType,
  studentFamilyMembers,
  defaultStudentNameOrderPreference,
  isLoading,
  courses,
  familyStudents,
  t,
  id,
  alerts,
  isStudentAlertsEnabled,
}) => {
  switch (userType) {
    case STAFF:
      if (_.isUndefined(courses) && isLoading) {
        return <SkeletonLoader height={20} width="100%" />;
      }

      return (
        <div className={classes.accessPointFooter}>
          <div className={classes.footerCountText}>
            <div className="text-label-s">
              {`${t("common:class_other")} (${_.size(courses)})`}
            </div>
          </div>
          <div className={classNames(classes.footerTextList, "text-body-s")}>
            <MoreItemWithToolTip
              items={_.map(courses, "title")}
              length={3}
              overrideClasses={moreItemOverrideClasses}
              tooltipOverlayStyle={styles.tooltipOverlayStyle}
            />
          </div>
        </div>
      );

    case STUDENT: {
      if ((_.isUndefined(studentFamilyMembers) || !alerts) && isLoading) {
        return <SkeletonLoader height={40} width="100%" />;
      }

      if (_.isEmpty(studentFamilyMembers) && _.isEmpty(alerts)) {
        return null;
      }
      const familyMembersCount = _.size(studentFamilyMembers);

      const familyMemberNames = _.map(studentFamilyMembers, familyMember =>
        getFormattedNameForUserType({
          user: { ...familyMember, type: PARENT },
        })
      );

      const joinedFamilyMemberNames = t("common:join_array_with_and", {
        array: familyMemberNames,
      });

      return (
        <div className={classes.footerContainer}>
          {isStudentAlertsEnabled && !_.isEmpty(alerts) ? (
            <StudentAlertsSection alerts={alerts} />
          ) : null}
          {familyMembersCount > 0 ? (
            <div className={classes.footerSection}>
              <div className={classes.accessPointFooter}>
                <div className={classes.footerCountText}>
                  <div className="text-label-s">
                    {`${t("common:family")} (${familyMembersCount})`}
                  </div>
                </div>
                <div
                  className={classNames(classes.footerTextList, "text-body-s")}
                >
                  {joinedFamilyMemberNames}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    case PARENT: {
      if (_.isUndefined(familyStudents) && isLoading) {
        return <SkeletonLoader height={20} width="100%" />;
      }

      const familyStudentsNames = _.map(familyStudents, student => {
        const user = {
          ...student,
          type: STUDENT,
        };
        const name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameOrderPreference,
        });

        const grade = _.get(student, "yearGroup.grade.name");

        return grade ? `${name} (${grade})` : name;
      });

      const joinedFamilyStudentsNames = t("common:join_array_with_and", {
        array: familyStudentsNames,
      });

      return (
        <div className={classes.accessPointFooter}>
          <div className={classes.footerCountText}>
            <div className="text-label-s">
              {`${t("common:children")} (${_.size(familyStudents)})`}
            </div>
          </div>
          <div className={classNames(classes.footerTextList, "text-body-s")}>
            {joinedFamilyStudentsNames}
          </div>
        </div>
      );
    }
  }
};

export const getUserActions = ({
  t,
  id,
  name,
  profileImage,
  userType,
  studentFamilyMembers,
  familyStudents,
  defaultStudentNameOrderPreference,
  onMessagingClick,
  currentUserId,
  isArchived,
  closePopover,
  isLoading,
  hideChatWithUser,
}) => {
  switch (userType) {
    case STAFF: {
      const onStaffMessageClick = () => {
        closePopover();
        onMessagingClick({ id });
        recordMessagingModuleOpened({
          from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
        });
      };
      const showCreateMessage =
        !isIdEqual(id, currentUserId) &&
        isPaidConnectModule() &&
        !hideChatWithUser;

      return showCreateMessage ? (
        <IconButton
          size="small"
          dsVersion="2.0"
          icon={<CommentSquareLeftClassroomOutlined />}
          variant="neutral"
          type="outlined"
          onClick={onStaffMessageClick}
        />
      ) : null;
    }

    case PARENT: {
      const studentsList = _.map(familyStudents, student => {
        const user = {
          ...student,
          type: STUDENT,
        };
        const name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameOrderPreference,
        });
        const {
          isArchived,
          id,
          profileImage,
          isFamilyChannelAllowed,
          familyMembers,
          alertTypes = [],
        } = student;

        return {
          id,
          name,
          isArchived,
          profileImage,
          isFamilyChannelAllowed,
          familyMembers,
          alertTypes,
        };
      });

      const parentInfo = {
        id,
        name,
        profileImage,
        isArchived,
      };

      const channelStudent = _.filter(studentsList, student =>
        _.get(student, "isFamilyChannelAllowed", false)
      );

      const familyChannels = _.map(channelStudent, student => {
        const { name, familyMembers } = student;
        return {
          label: t("common:family_of_label", {
            label: name,
          }),
          id: _.get(student, "id"),
          membersCount: _.size(familyMembers),
        };
      });

      const onChannelClick = id => {
        closePopover();
        onMessagingClick({
          id,
          isFamilyChannel: true,
        });
        recordMessagingModuleOpened({
          from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
        });
      };

      const onUserClick = userId => {
        closePopover();
        onMessagingClick({
          id: userId,
          isFamilyChannel: false,
        });
        recordMessagingModuleOpened({
          from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
        });
      };

      const isFamilyListLoading =
        _.isUndefined(studentFamilyMembers) && isLoading;

      const hideAction = hideChatWithUser && _.isEmpty(studentsList);

      if (hideAction) {
        return null;
      }

      return (
        <Dropdown
          overlay={
            <ParentDropdownPopoverContent
              parentInfo={parentInfo}
              studentsList={channelStudent}
              isStudentListLoading={isFamilyListLoading}
              familyChannels={familyChannels}
              onUserClick={onUserClick}
              onChannelClick={onChannelClick}
              hideChatWithUser={hideChatWithUser}
            />
          }
          placement="bottomLeft"
        >
          <ButtonDisclosure
            dsVersion="2.0"
            icon={<CommentSquareLeftClassroomOutlined />}
            size="small"
            variant="neutral"
            type="outlined"
          />
        </Dropdown>
      );
    }

    case STUDENT: {
      const familyList = _.map(studentFamilyMembers, familyMember => {
        const name = getFormattedNameForUserType({
          user: { ...familyMember, type: PARENT },
        });
        const { isArchived, id, profileImage } = familyMember;

        return {
          id,
          name,
          isArchived,
          profileImage,
        };
      });

      const studentInfo = {
        id,
        name,
        profileImage,
        isArchived,
      };

      const familyChannels = [
        {
          label: t("common:family_of_label", {
            label: name,
          }),
          id,
          membersCount: _.size(familyList),
        },
      ];

      const onUserClick = userId => {
        closePopover();
        onMessagingClick({
          id: userId,
          isFamilyChannel: false,
        });
        recordMessagingModuleOpened({
          from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
        });
      };

      const isFamilyListLoading =
        _.isUndefined(studentFamilyMembers) && isLoading;

      const onChannelClick = id => {
        closePopover();
        onMessagingClick({
          id,
          isFamilyChannel: true,
        });
        recordMessagingModuleOpened({
          from: MESSAGING_MODULE_OPENED_FROM.GLOBAL_ACCESS_POINT,
        });
      };

      const hideAction =
        (!isPaidConnectModule() || hideChatWithUser) && _.isEmpty(familyList);

      if (hideAction) {
        return null;
      }

      return (
        <Dropdown
          overlay={
            <StudentDropdownPopoverContent
              studentInfo={studentInfo}
              familyList={familyList}
              isFamilyListLoading={isFamilyListLoading}
              familyChannels={familyChannels}
              onUserClick={onUserClick}
              onChannelClick={onChannelClick}
              hideChatWithUser={hideChatWithUser}
            />
          }
          placement="bottomLeft"
        >
          <ButtonDisclosure
            dsVersion="2.0"
            icon={<CommentSquareLeftClassroomOutlined />}
            size="small"
            variant="neutral"
            type="outlined"
          />
        </Dropdown>
      );
    }
  }
};

export const getSubTitle1Component = ({ subtitle, isLoading }) => {
  if (_.isUndefined(subtitle) && isLoading) {
    return <SkeletonLoader height={20} width="50%" />;
  }
  return subtitle;
};

export const isAccessPointEnable = (userType, module) => {
  // current user not staff then disable extra check
  if (!_.isEqual(getUserType(), STAFF)) return false;

  if (!isMessagingV2Enabled()) return false;

  if (module) {
    const permission = ACCESS_POINT_PERMISSION[module];
    if (permission && !ACLStore.can(permission)) {
      return false;
    }
  }

  switch (userType) {
    case STAFF:
      return showTeacherMessagingAccessPoint();
    case STUDENT:
      return showStudentMessagingAccessPoint();
    case PARENT:
      return showFamilyMessagingAccessPoint();
  }
  return false;
};

export const getSubtitle1WithData = ({
  userType,
  allGrades,
  displayRole,
  t,
}) => {
  switch (userType) {
    case STAFF:
      return displayRole;

    case PARENT:
      return t("common:family_member");

    case STUDENT:
      return !_.isEmpty(allGrades) ? (
        <div className={classes.subtitleContainer}>
          <div>{t("common:student")}</div>
          <div className={classes.circularDivider} />
          <div>
            {t("common:join_array_with_and", {
              array: _.map(allGrades, "name"),
            })}
          </div>
        </div>
      ) : undefined;
  }
};
